import type { Locale } from "./i18n";

export const threadsCompatibilityLocales = ["en", "zh-tw", "ja"] as const;
export type ThreadsCompatibilityLocale = (typeof threadsCompatibilityLocales)[number];
export type CompatibilityTone = "supported" | "conditional" | "rejected";

export const threadsCompatibilityPublishedAt = "2026-09-03";
export const threadsCompatibilityUpdatedAt = "2026-09-03";

export interface CompatibilityFact {
  value: string;
  label: string;
  detail: string;
}

export interface CompatibilityRow {
  item: string;
  example: string;
  status: string;
  tone: CompatibilityTone;
  result: string;
}

export interface CompatibilitySource {
  title: string;
  publisher: string;
  url: string;
}

export interface ThreadsCompatibilityContent {
  navigationLabel: string;
  breadcrumbLabel: string;
  eyebrow: string;
  title: string;
  seoTitle: string;
  seoDescription: string;
  lead: string;
  byline: string;
  publishedLabel: string;
  updatedLabel: string;
  directTitle: string;
  directAnswer: string;
  snapshotTitle: string;
  snapshotLead: string;
  facts: readonly [CompatibilityFact, CompatibilityFact, CompatibilityFact];
  inputTitle: string;
  inputLead: string;
  inputColumns: readonly [string, string, string, string];
  inputRows: readonly CompatibilityRow[];
  outputTitle: string;
  outputLead: string;
  outputColumns: readonly [string, string, string, string];
  outputRows: readonly CompatibilityRow[];
  limitsTitle: string;
  limits: readonly string[];
  methodTitle: string;
  methodParagraphs: readonly [string, string, string];
  changesTitle: string;
  changes: readonly { date: string; text: string }[];
  sourcesTitle: string;
  sources: readonly CompatibilitySource[];
  ctaTitle: string;
  ctaText: string;
  ctaLabel: string;
  previewKicker: string;
  previewTitle: string;
  previewLead: string;
  previewFacts: readonly [string, string, string];
  previewLink: string;
  visualLabels: readonly [string, string, string];
}

const sharedSources: readonly CompatibilitySource[] = [
  {
    title: "Introducing Threads: A New Way to Share With Text",
    publisher: "Meta Newsroom",
    url: "https://about.fb.com/news/2023/07/introducing-threads-new-app-text-sharing/"
  },
  {
    title: "New Features for the Threads Web Experience",
    publisher: "Meta Newsroom",
    url: "https://about.fb.com/news/2025/04/new-features-threads-web-experience/"
  },
  {
    title: "SiloSave privacy policy",
    publisher: "SiloSave",
    url: "https://silosave.com/privacy"
  }
];

const content: Record<ThreadsCompatibilityLocale, ThreadsCompatibilityContent> = {
  en: {
    navigationLabel: "Compatibility",
    breadcrumbLabel: "Threads compatibility",
    eyebrow: "CODE-BACKED REFERENCE",
    title: "Threads downloader compatibility",
    seoTitle: "Threads Downloader Compatibility & Limits | SiloSave",
    seoDescription: "See which Threads post, profile and share links SiloSave accepts, what it exports, when results may be partial, and which privacy and ZIP limits apply.",
    lead: "A maintained reference for accepted public Threads URLs, returned content, export formats, failure states, and the direct media path used by SiloSave.",
    byline: "SiloSave Editorial Team",
    publishedLabel: "Published",
    updatedLabel: "Last reviewed",
    directTitle: "What does the Threads downloader support?",
    directAnswer: "SiloSave accepts an exact public Threads post URL, a public profile URL, or a Threads share URL. It can return the post text, available photos and videos, quoted-post context, public replies exposed by Threads, and public profile posts. It does not bypass private, deleted, login-only, age-restricted, or otherwise unavailable content.",
    snapshotTitle: "Current compatibility snapshot",
    snapshotLead: "These are product capabilities, not marketing estimates. The page is updated when URL validation, resolver behavior, export formats, or browser limits change.",
    facts: [
      { value: "3", label: "accepted URL types", detail: "Exact post, public profile, and Threads share link." },
      { value: "5", label: "text export choices", detail: "Copy, TXT, HTML, JSON, and CSV." },
      { value: "0", label: "media files stored", detail: "Photos and videos travel from Meta CDN to the browser." }
    ],
    inputTitle: "Supported Threads URL types",
    inputLead: "The validator accepts only narrow, recognizable public routes. Tracking parameters may be ignored, but credentials, explicit ports, unrelated product routes, and lookalike hosts are rejected.",
    inputColumns: ["Input", "Example pattern", "Status", "What happens"],
    inputRows: [
      {
        item: "Exact public post",
        example: "threads.com/@user/post/POST_ID",
        status: "Supported",
        tone: "supported",
        result: "The exact post is normalized to threads.com and resolved without crawling a feed or profile timeline."
      },
      {
        item: "Public profile",
        example: "threads.com/@user",
        status: "Supported",
        tone: "supported",
        result: "SiloSave returns the public profile details and the posts Threads currently exposes, with a complete or partial label."
      },
      {
        item: "Threads share link",
        example: "threads.com/share/SHARE_ID",
        status: "Supported",
        tone: "conditional",
        result: "The share URL is followed only to a validated Threads destination. A different host or unexpected target is rejected."
      },
      {
        item: "Feed, search, replies tab, or private URL",
        example: "threads.com/explore or /@user/replies",
        status: "Rejected",
        tone: "rejected",
        result: "SiloSave does not crawl timelines, search results, private areas, or unsupported account routes."
      }
    ],
    outputTitle: "Content and export compatibility",
    outputLead: "Availability depends on what Threads exposes for that public item at request time. SiloSave labels partial data rather than inventing missing posts, replies, or files.",
    outputColumns: ["Content", "Available action", "Status", "Important limit"],
    outputRows: [
      {
        item: "Post text",
        example: "Copy, TXT, HTML, JSON, CSV",
        status: "Supported",
        tone: "supported",
        result: "Text-only posts are valid results. Main text, quoted posts, and replies stay structurally separate."
      },
      {
        item: "Photos and carousels",
        example: "Individual originals or browser ZIP",
        status: "Supported",
        tone: "conditional",
        result: "ZIP batches are limited to 10 files and 100 MiB. Browser CORS can block ZIP even when individual files still open."
      },
      {
        item: "Video",
        example: "Direct original-file save",
        status: "Supported",
        tone: "conditional",
        result: "Only files currently exposed by Threads are shown. SiloSave does not create a resolution or audio track that is absent upstream."
      },
      {
        item: "Public replies",
        example: "Nested reply view plus text exports",
        status: "May be partial",
        tone: "conditional",
        result: "When Threads reports more replies than its public response returns, the loaded and expected counts are shown separately."
      },
      {
        item: "Quoted post",
        example: "Separate text and media context",
        status: "Supported when exposed",
        tone: "conditional",
        result: "Quoted content is not merged into the main post, so the source relationship remains visible."
      },
      {
        item: "Public profile export",
        example: "Copy, TXT, HTML, JSON, CSV, media ZIP batches",
        status: "Supported",
        tone: "conditional",
        result: "The export includes only profile posts present in the current public response and marks incomplete timelines explicitly."
      }
    ],
    limitsTitle: "Known limits and failure states",
    limits: [
      "Private, deleted, suspended, login-only, age-restricted, or region-restricted content cannot be opened.",
      "Threads can temporarily block automated public-page access. SiloSave reports an upstream-blocked state instead of retrying indefinitely.",
      "A platform page-format change can stop parsing until the resolver is updated. SiloSave rejects ambiguous media rather than guessing.",
      "Public reply and profile totals can be larger than the content returned to a logged-out request. Partial results remain usable but are labeled partial.",
      "Temporary Meta CDN URLs can expire. Resolving the source again is safer than reusing an old media URL.",
      "Browser CORS rules can prevent a multi-file ZIP. Individual direct links may still work because the browser opens the original CDN file."
    ],
    methodTitle: "How this reference is kept accurate",
    methodParagraphs: [
      "The accepted URL patterns mirror the same frontend validator and backend normalizer used before a request reaches the resolver. Tests cover exact posts, public profiles, share URLs, lookalike hosts, credentials, ports, unsupported paths, and canonical conversion from threads.net to threads.com.",
      "The output matrix mirrors the response model and browser export code. It distinguishes post, profile, quote, reply, image, video, text, and ZIP behavior, including the conditions that produce partial results or browser-only failures.",
      "This page does not publish a fabricated success-rate percentage. Reliability depends on current Threads behavior, public availability, region, and browser rules. Changes are recorded below when the implemented contract changes."
    ],
    changesTitle: "Change log",
    changes: [
      { date: "2026-09-03", text: "Reviewed exact post, public profile, share-link, reply-completeness, direct-media, and browser ZIP behavior against the current implementation and regression tests." }
    ],
    sourcesTitle: "Reference sources",
    sources: sharedSources,
    ctaTitle: "Have a supported public Threads link?",
    ctaText: "Open the downloader, paste one exact post, profile, or share URL, then keep only the text or media you are permitted to save.",
    ctaLabel: "Open Threads downloader",
    previewKicker: "02 / VERIFIED SCOPE",
    previewTitle: "Know what will work before you paste.",
    previewLead: "The compatibility reference documents accepted Threads URLs, export formats, partial-result rules, browser ZIP limits, and the direct media path.",
    previewFacts: ["Exact posts, profiles, and share links", "TXT, HTML, JSON, CSV, and ZIP rules", "Private and partial-result limits stated clearly"],
    previewLink: "View compatibility matrix",
    visualLabels: ["Public URL", "Validated result", "Browser save"]
  },
  "zh-tw": {
    navigationLabel: "支援範圍",
    breadcrumbLabel: "Threads 支援範圍",
    eyebrow: "以程式與測試為準",
    title: "Threads 下載器支援範圍",
    seoTitle: "Threads 下載器支援範圍與限制｜SiloSave",
    seoDescription: "查看 SiloSave 支援哪些 Threads 貼文、個人頁與分享網址，可匯出哪些內容，何時會標示部分結果，以及隱私與 ZIP 限制。",
    lead: "整理 SiloSave 實際接受的公開 Threads 網址、可取得內容、匯出格式、失敗狀態與媒體直連方式，功能變更時同步更新。",
    byline: "SiloSave 編輯團隊",
    publishedLabel: "發布",
    updatedLabel: "最後檢查",
    directTitle: "Threads 下載器實際支援什麼？",
    directAnswer: "SiloSave 接受單一公開 Threads 貼文網址、公開個人頁網址與 Threads 分享網址，可取得貼文文字、公開照片與影片、引用貼文、Threads 實際公開的回覆，以及公開個人頁貼文。私人、已刪除、需登入、年齡限制或其他無法公開存取的內容不會被繞過。",
    snapshotTitle: "目前支援快照",
    snapshotLead: "以下是產品目前實作的能力，不是推估或宣傳數字。網址驗證、解析流程、匯出格式或瀏覽器限制改變時，這一頁會跟著更新。",
    facts: [
      { value: "3", label: "種可接受網址", detail: "單一貼文、公開個人頁、Threads 分享連結。" },
      { value: "5", label: "種文字匯出", detail: "複製、TXT、HTML、JSON、CSV。" },
      { value: "0", label: "個永久媒體副本", detail: "照片與影片由 Meta CDN 直接送到瀏覽器。" }
    ],
    inputTitle: "支援的 Threads 網址類型",
    inputLead: "驗證器只接受範圍明確、可以辨識的公開路徑。追蹤參數可被忽略，但帳密、明確連接埠、無關產品路徑與仿冒網域會被拒絕。",
    inputColumns: ["輸入類型", "網址格式", "狀態", "處理方式"],
    inputRows: [
      {
        item: "單一公開貼文",
        example: "threads.com/@使用者/post/貼文ID",
        status: "支援",
        tone: "supported",
        result: "只解析該則貼文並正規化為 threads.com，不會順便爬取動態牆或整個個人頁。"
      },
      {
        item: "公開個人頁",
        example: "threads.com/@使用者",
        status: "支援",
        tone: "supported",
        result: "回傳公開個人資料與 Threads 當下實際提供的貼文，並清楚標示完整或部分結果。"
      },
      {
        item: "Threads 分享連結",
        example: "threads.com/share/分享ID",
        status: "條件支援",
        tone: "conditional",
        result: "只允許展開到經驗證的 Threads 目的地；若跳到其他網域或非預期頁面就拒絕。"
      },
      {
        item: "動態牆、搜尋、回覆分頁或私人網址",
        example: "threads.com/explore 或 /@使用者/replies",
        status: "不支援",
        tone: "rejected",
        result: "不爬取時間軸、搜尋結果、私人區域或其他未支援的帳號路徑。"
      }
    ],
    outputTitle: "內容與匯出相容性",
    outputLead: "能取得哪些內容，取決於 Threads 在請求當下對該公開項目實際提供什麼。SiloSave 會標示不完整，不會補猜缺少的貼文、回覆或檔案。",
    outputColumns: ["內容", "可用操作", "狀態", "重要限制"],
    outputRows: [
      {
        item: "貼文文字",
        example: "複製、TXT、HTML、JSON、CSV",
        status: "支援",
        tone: "supported",
        result: "純文字貼文也是有效結果；主貼文、引用貼文與回覆會保持分離。"
      },
      {
        item: "照片與輪播",
        example: "逐張原始檔或瀏覽器 ZIP",
        status: "條件支援",
        tone: "conditional",
        result: "每個 ZIP 最多 10 個檔案、100 MiB；即使單檔可開啟，瀏覽器 CORS 仍可能阻擋 ZIP。"
      },
      {
        item: "影片",
        example: "直接保存原始檔",
        status: "條件支援",
        tone: "conditional",
        result: "只顯示 Threads 當下提供的檔案，不會憑空產生上游不存在的解析度或音軌。"
      },
      {
        item: "公開回覆",
        example: "巢狀檢視與文字匯出",
        status: "可能不完整",
        tone: "conditional",
        result: "若 Threads 回報的回覆數大於公開回應實際傳回數量，會分別顯示已載入與預期數量。"
      },
      {
        item: "引用貼文",
        example: "獨立顯示文字與媒體",
        status: "上游提供時支援",
        tone: "conditional",
        result: "引用內容不會混進主貼文，來源關係仍可辨識。"
      },
      {
        item: "公開個人頁匯出",
        example: "複製、TXT、HTML、JSON、CSV、媒體 ZIP 分包",
        status: "條件支援",
        tone: "conditional",
        result: "只包含目前公開回應中的個人頁貼文，若時間軸不完整會明確標示。"
      }
    ],
    limitsTitle: "已知限制與失敗狀態",
    limits: [
      "私人、已刪除、停權、需登入、年齡限制或地區限制內容無法開啟。",
      "Threads 可能暫時阻擋公開頁讀取；SiloSave 會回報上游阻擋，不會無限重試。",
      "平台頁面格式改變時，解析可能暫停到程式更新；SiloSave 會拒絕不明確媒體，不會用猜的。",
      "公開回覆或個人頁總數可能高於未登入請求實際取得的內容；部分結果仍可使用，但會標示不完整。",
      "Meta CDN 暫時網址會失效；重新解析來源比重複使用舊媒體網址可靠。",
      "瀏覽器 CORS 可能阻止多檔 ZIP；單一直接連結仍可能正常，因為瀏覽器是開啟原始 CDN 檔案。"
    ],
    methodTitle: "這份支援表如何維持準確",
    methodParagraphs: [
      "可接受網址格式直接對應下載器使用的前端驗證與後端正規化規則。測試涵蓋單一貼文、公開個人頁、分享網址、仿冒網域、帳密、連接埠、不支援路徑，以及 threads.net 轉為 threads.com 的 canonical 行為。",
      "輸出表對應實際回應資料與瀏覽器匯出程式，分開描述貼文、個人頁、引用、回覆、圖片、影片、文字與 ZIP，並納入部分結果與瀏覽器端失敗條件。",
      "這一頁不捏造成功率百分比。可靠度會受到 Threads 當下行為、公開狀態、地區與瀏覽器規則影響；實作契約改變時會在下方留下紀錄。"
    ],
    changesTitle: "變更紀錄",
    changes: [
      { date: "2026-09-03", text: "依目前程式與回歸測試，重新檢查單一貼文、公開個人頁、分享連結、回覆完整度、媒體直連與瀏覽器 ZIP 行為。" }
    ],
    sourcesTitle: "參考來源",
    sources: sharedSources,
    ctaTitle: "手上有支援的公開 Threads 網址？",
    ctaText: "開啟下載器，貼上一個單一貼文、個人頁或分享網址，只保存你擁有或獲准保留的文字與媒體。",
    ctaLabel: "開啟 Threads 下載器",
    previewKicker: "02 / 已驗證範圍",
    previewTitle: "貼上前，先知道哪些情況會成功。",
    previewLead: "支援表整理可接受的 Threads 網址、匯出格式、部分結果規則、瀏覽器 ZIP 限制與媒體直連方式。",
    previewFacts: ["單一貼文、公開個人頁與分享連結", "TXT、HTML、JSON、CSV 與 ZIP 規則", "私人內容與部分結果限制清楚標示"],
    previewLink: "查看完整支援表",
    visualLabels: ["公開網址", "驗證結果", "瀏覽器保存"]
  },
  ja: {
    navigationLabel: "対応範囲",
    breadcrumbLabel: "Threads対応範囲",
    eyebrow: "コードとテストに基づく情報",
    title: "Threadsダウンローダーの対応範囲",
    seoTitle: "Threadsダウンローダーの対応範囲と制限｜SiloSave",
    seoDescription: "SiloSaveが対応するThreads投稿・公開プロフィール・共有URL、保存形式、部分取得の扱い、プライバシーとZIPの制限を確認できます。",
    lead: "SiloSaveが実際に受け付ける公開Threads URL、取得内容、書き出し形式、失敗状態、メディアの直接配信経路をまとめた更新型リファレンスです。",
    byline: "SiloSave編集チーム",
    publishedLabel: "公開日",
    updatedLabel: "最終確認",
    directTitle: "Threadsダウンローダーは何に対応していますか？",
    directAnswer: "SiloSaveは、公開Threads投稿の個別URL、公開プロフィールURL、Threads共有URLに対応します。投稿本文、公開されている写真や動画、引用投稿、Threadsが公開レスポンスで返す返信、公開プロフィールの投稿を取得できます。非公開、削除済み、ログイン必須、年齢制限など、公開アクセスできない内容を回避して取得することはありません。",
    snapshotTitle: "現在の対応状況",
    snapshotLead: "以下は推定値ではなく、現在の製品実装に基づく内容です。URL検証、解析処理、書き出し形式、ブラウザ制限が変わった場合に更新します。",
    facts: [
      { value: "3", label: "種類の入力URL", detail: "個別投稿、公開プロフィール、Threads共有リンク。" },
      { value: "5", label: "種類のテキスト保存", detail: "コピー、TXT、HTML、JSON、CSV。" },
      { value: "0", label: "件の恒久メディア保存", detail: "写真と動画はMeta CDNからブラウザへ直接届きます。" }
    ],
    inputTitle: "対応するThreads URL",
    inputLead: "検証処理は、用途が明確で識別可能な公開パスだけを受け付けます。追跡パラメータは無視できますが、認証情報、明示的なポート、無関係な機能ページ、偽装ホストは拒否します。",
    inputColumns: ["入力", "URL形式", "状態", "処理内容"],
    inputRows: [
      {
        item: "公開投稿の個別URL",
        example: "threads.com/@user/post/POST_ID",
        status: "対応",
        tone: "supported",
        result: "対象投稿だけをthreads.com形式に正規化して解析し、フィードやプロフィール全体は巡回しません。"
      },
      {
        item: "公開プロフィール",
        example: "threads.com/@user",
        status: "対応",
        tone: "supported",
        result: "公開プロフィール情報と、その時点でThreadsが返す投稿を取得し、完全か部分取得かを表示します。"
      },
      {
        item: "Threads共有リンク",
        example: "threads.com/share/SHARE_ID",
        status: "条件付き対応",
        tone: "conditional",
        result: "検証済みのThreads宛先に限って展開します。別ホストや想定外の移動先は拒否します。"
      },
      {
        item: "フィード、検索、返信タブ、非公開URL",
        example: "threads.com/explore または /@user/replies",
        status: "非対応",
        tone: "rejected",
        result: "タイムライン、検索結果、非公開領域、未対応のアカウントパスは巡回しません。"
      }
    ],
    outputTitle: "取得内容と保存形式",
    outputLead: "取得できる内容は、リクエスト時点でThreadsがその公開項目に対して返す情報に依存します。不足分を推測せず、部分取得であることを明示します。",
    outputColumns: ["内容", "操作", "状態", "主な制限"],
    outputRows: [
      {
        item: "投稿本文",
        example: "コピー、TXT、HTML、JSON、CSV",
        status: "対応",
        tone: "supported",
        result: "テキストのみの投稿も有効です。本文、引用投稿、返信は別構造のまま保存されます。"
      },
      {
        item: "写真とカルーセル",
        example: "個別の元ファイルまたはブラウザZIP",
        status: "条件付き対応",
        tone: "conditional",
        result: "ZIPは1回10ファイル、100 MiBまでです。個別表示が可能でもCORSによりZIPだけ失敗する場合があります。"
      },
      {
        item: "動画",
        example: "元ファイルを直接保存",
        status: "条件付き対応",
        tone: "conditional",
        result: "Threadsが現在公開しているファイルのみ表示します。存在しない解像度や音声を生成しません。"
      },
      {
        item: "公開返信",
        example: "階層表示とテキスト書き出し",
        status: "部分取得の場合あり",
        tone: "conditional",
        result: "Threadsの報告件数が公開レスポンスの取得件数より多い場合、取得数と期待数を分けて表示します。"
      },
      {
        item: "引用投稿",
        example: "本文とメディアを別表示",
        status: "公開時に対応",
        tone: "conditional",
        result: "引用内容を本文へ混在させず、元の関係が分かる形で扱います。"
      },
      {
        item: "公開プロフィール書き出し",
        example: "コピー、TXT、HTML、JSON、CSV、メディアZIP分割",
        status: "条件付き対応",
        tone: "conditional",
        result: "現在の公開レスポンスに含まれる投稿だけを保存し、タイムラインが不完全な場合は明示します。"
      }
    ],
    limitsTitle: "既知の制限と失敗状態",
    limits: [
      "非公開、削除済み、停止済み、ログイン必須、年齢制限、地域制限のある内容は取得できません。",
      "Threadsが公開ページへのアクセスを一時的に拒否することがあります。SiloSaveは無限再試行せず、上流ブロックとして報告します。",
      "ページ形式が変わると解析が更新まで停止する場合があります。不明確なメディアを推測して表示しません。",
      "公開返信数やプロフィール投稿数が、ログアウト状態で返される実データより多い場合があります。部分結果は利用できますが明示されます。",
      "Meta CDNの一時URLは期限切れになります。古いURLを使い続けるより、元の投稿を再解析してください。",
      "ブラウザのCORS制限で複数ファイルZIPが失敗することがあります。元CDNを開く個別リンクは利用できる場合があります。"
    ],
    methodTitle: "このページを正確に保つ方法",
    methodParagraphs: [
      "対応URLは、実際のダウンローダーが使用するフロントエンド検証とバックエンド正規化に合わせています。個別投稿、公開プロフィール、共有URL、偽装ホスト、認証情報、ポート、未対応パス、threads.netからthreads.comへのcanonical変換をテストします。",
      "出力表はレスポンスモデルとブラウザ書き出し処理に対応し、投稿、プロフィール、引用、返信、画像、動画、テキスト、ZIPを分けて記載しています。部分取得とブラウザ側の失敗条件も対象です。",
      "根拠のない成功率は掲載しません。信頼性はThreadsの現行仕様、公開状態、地域、ブラウザ規則に左右されます。実装上の契約が変わった場合は下記の変更履歴に記録します。"
    ],
    changesTitle: "変更履歴",
    changes: [
      { date: "2026-09-03", text: "現行実装と回帰テストに基づき、個別投稿、公開プロフィール、共有リンク、返信の完全性、メディア直接配信、ブラウザZIP動作を再確認しました。" }
    ],
    sourcesTitle: "参考情報",
    sources: sharedSources,
    ctaTitle: "対応する公開Threads URLをお持ちですか？",
    ctaText: "ダウンローダーを開き、個別投稿、プロフィール、共有URLのいずれかを貼り付け、保存権限のある本文やメディアだけを保存してください。",
    ctaLabel: "Threadsダウンローダーを開く",
    previewKicker: "02 / 検証済み範囲",
    previewTitle: "貼り付ける前に、対応条件を確認できます。",
    previewLead: "対応URL、保存形式、部分取得の扱い、ブラウザZIPの制限、メディアの直接配信経路を一覧にしました。",
    previewFacts: ["個別投稿・公開プロフィール・共有リンク", "TXT・HTML・JSON・CSV・ZIPの条件", "非公開と部分取得の制限を明示"],
    previewLink: "対応表を見る",
    visualLabels: ["公開URL", "検証済み結果", "ブラウザ保存"]
  }
};

export function isThreadsCompatibilityLocale(locale: Locale): locale is ThreadsCompatibilityLocale {
  return threadsCompatibilityLocales.includes(locale as ThreadsCompatibilityLocale);
}

export function getThreadsCompatibilityContent(locale: Locale): ThreadsCompatibilityContent {
  return isThreadsCompatibilityLocale(locale) ? content[locale] : content.en;
}
